import { zodResolver } from '@hookform/resolvers/zod';
import React, { useCallback, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import { AIExtractedIndicator } from '@/components/postgrid/common/AIExtractedIndicator';
import { WizardStep } from '@/components/postgrid/wizard';
import { AddressAutocomplete } from '@/components/ui/address-autocomplete/AddressAutocomplete';
import { US_STATE_OPTIONS } from '@/components/ui/address-autocomplete/constants';
import {
  Field,
  FieldGroup,
  Fieldset,
  Label,
} from '@/components/ui/catalyst/fieldset';
import { Input } from '@/components/ui/catalyst/input';
import { Select } from '@/components/ui/catalyst/select';
import { Textarea } from '@/components/ui/catalyst/textarea';

const addressSchema = z.object({
  address_line1: z.string().min(1, 'Street address is required'),
  address_line2: z.string().optional(),
  city: z.string().min(1, 'City is required'),
  province_or_state: z.string().min(1, 'State or province is required'),
  postal_or_zip: z.string().min(1, 'ZIP or postal code is required'),
  country: z.string().min(1, 'Country is required'),
  notes: z.string().optional(),
});

export type AddressStepData = z.infer<typeof addressSchema>;

interface ContactAddressStepProps {
  wizardData?: Partial<AddressStepData>;
  updateStepData?: (data: Record<string, unknown>) => void;
  markStepComplete?: (isValid: boolean) => void;
  /** Set of field names that were AI-extracted */
  aiExtractedFields?: Set<string>;
}

export const ContactAddressStep: React.FC<ContactAddressStepProps> = ({
  wizardData = {},
  updateStepData,
  markStepComplete,
  aiExtractedFields = new Set(),
}) => {
  const {
    register,
    watch,
    setValue,
    formState: { errors, isValid },
  } = useForm<AddressStepData>({
    resolver: zodResolver(addressSchema),
    mode: 'onChange',
    defaultValues: {
      address_line1: wizardData.address_line1 ?? '',
      address_line2: wizardData.address_line2 ?? '',
      city: wizardData.city ?? '',
      province_or_state: wizardData.province_or_state ?? '',
      postal_or_zip: wizardData.postal_or_zip ?? '',
      country: wizardData.country ?? 'US',
      notes: wizardData.notes ?? '',
    },
  });

  const watched = watch();
  const country = watch('country');

  // Handle autocomplete selection - fills all address fields
  const handleAddressSelect = useCallback(
    (address: {
      address_line1: string;
      address_line2: string | null;
      city: string;
      state: string;
      postal_code: string;
    }) => {
      setValue('address_line1', address.address_line1, {
        shouldValidate: true,
      });
      setValue('address_line2', address.address_line2 || '', {
        shouldValidate: true,
      });
      setValue('city', address.city, { shouldValidate: true });
      setValue('province_or_state', address.state, { shouldValidate: true });
      setValue('postal_or_zip', address.postal_code, { shouldValidate: true });
    },
    [setValue],
  );

  // Handle manual street address changes
  const handleStreetAddressChange = useCallback(
    (value: string) => {
      setValue('address_line1', value, { shouldValidate: true });
    },
    [setValue],
  );

  useEffect(() => {
    // Always skip PostGrid address verification - we use Smarty for autocomplete
    // and want to preserve the user's exact address input
    updateStepData?.({ ...watched, skip_verification: true });
  }, [watched, updateStepData]);

  useEffect(() => {
    markStepComplete?.(isValid);
  }, [isValid, markStepComplete]);

  return (
    <WizardStep
      title="Mailing Address"
      description="Start typing to search for a verified address, or enter manually."
    >
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-6">
        <Fieldset>
          <FieldGroup className="space-y-4">
            <Field>
              <div className="flex items-center gap-2">
                <Label>Street Address *</Label>
                <AIExtractedIndicator
                  show={aiExtractedFields.has('address_line1')}
                />
              </div>
              {country === 'US' ? (
                <AddressAutocomplete
                  value={watched.address_line1}
                  onChange={handleStreetAddressChange}
                  onAddressSelect={handleAddressSelect}
                  placeholder="Start typing an address..."
                  inputClassName="bg-white"
                />
              ) : (
                <Input
                  {...register('address_line1')}
                  placeholder="123 Main St"
                />
              )}
              {errors.address_line1 && (
                <p className="text-sm text-red-600 mt-1">
                  {errors.address_line1.message}
                </p>
              )}
            </Field>

            <Field>
              <div className="flex items-center gap-2">
                <Label>Apartment, Suite, etc.</Label>
                <AIExtractedIndicator
                  show={aiExtractedFields.has('address_line2')}
                />
              </div>
              <Input {...register('address_line2')} placeholder="Apt 4B" />
            </Field>

            <div className="grid grid-cols-1 sm:grid-cols-6 gap-4">
              <div className="col-span-2 sm:col-span-3">
                <Field>
                  <div className="flex items-center gap-2">
                    <Label>City *</Label>
                    <AIExtractedIndicator
                      show={aiExtractedFields.has('city')}
                    />
                  </div>
                  <Input {...register('city')} placeholder="New York" />
                  {errors.city && (
                    <p className="text-sm text-red-600 mt-1">
                      {errors.city.message}
                    </p>
                  )}
                </Field>
              </div>

              <div className="col-span-1 sm:col-span-2">
                <Field>
                  <div className="flex items-center gap-2">
                    <Label>State *</Label>
                    <AIExtractedIndicator
                      show={aiExtractedFields.has('province_or_state')}
                    />
                  </div>
                  {country === 'US' ? (
                    <Select {...register('province_or_state')}>
                      <option value="">Select state</option>
                      {US_STATE_OPTIONS.map((state) => (
                        <option key={state.code} value={state.code}>
                          {state.code}
                        </option>
                      ))}
                    </Select>
                  ) : (
                    <Input
                      {...register('province_or_state')}
                      placeholder="Province"
                    />
                  )}
                  {errors.province_or_state && (
                    <p className="text-sm text-red-600 mt-1">
                      {errors.province_or_state.message}
                    </p>
                  )}
                </Field>
              </div>

              <div className="col-span-1 sm:col-span-1">
                <Field>
                  <div className="flex items-center gap-2">
                    <Label>ZIP *</Label>
                    <AIExtractedIndicator
                      show={aiExtractedFields.has('postal_or_zip')}
                    />
                  </div>
                  <Input {...register('postal_or_zip')} placeholder="10001" />
                  {errors.postal_or_zip && (
                    <p className="text-sm text-red-600 mt-1">
                      {errors.postal_or_zip.message}
                    </p>
                  )}
                </Field>
              </div>
            </div>

            <div className="w-48">
              <Field>
                <div className="flex items-center gap-2">
                  <Label>Country *</Label>
                  <AIExtractedIndicator
                    show={aiExtractedFields.has('country')}
                  />
                </div>
                <Select {...register('country')}>
                  <option value="US">United States</option>
                  <option value="CA">Canada</option>
                </Select>
                {errors.country && (
                  <p className="text-sm text-red-600 mt-1">
                    {errors.country.message}
                  </p>
                )}
              </Field>
            </div>

            <Field>
              <Label>Additional Notes</Label>
              <Textarea
                {...register('notes')}
                rows={3}
                placeholder="Delivery notes..."
              />
            </Field>
          </FieldGroup>
        </Fieldset>
      </div>
    </WizardStep>
  );
};
